import React from "react";
import { C } from "../theme";

// Shared action button for text CTAs across the app. Enforces a single,
// consistent look (uppercase label, serif font, rounded shape) so buttons no
// longer drift between ALL-CAPS/Title-case and one-off inline styles.
//
// Variants:
//   primary   – filled gradient, main call-to-action
//   secondary – outlined (transparent bg + seafoam border)
//   danger    – red gradient, destructive action
//   ghost     – subtle transparent, low-emphasis action
//
// Notes:
//   - The label is uppercased via CSS (textTransform), so children can stay in
//     normal case in JSX.
//   - `fullWidth` stretches to 100%. Pass `style` to override anything.
//   - Icon-only buttons, expand/collapse toggles, and inline text links are NOT
//     meant to use this component.

const SIZES = {
  md: { padding: "13px 18px", fontSize: 13 },
  sm: { padding: "9px 14px", fontSize: 12 },
};

function getVariantStyle(variant) {
  switch (variant) {
    case "secondary":
      return {
        background: "transparent",
        color: C.white,
        border: `1px solid ${C.seafoam}66`,
      };
    case "danger":
      return {
        background: `linear-gradient(135deg, ${C.red}, #7a1c1c)`,
        color: "#FFFFFF",
        border: "none",
      };
    case "ghost":
      return {
        background: "transparent",
        color: C.muted,
        border: `1px solid ${C.border}`,
      };
    case "primary":
    default:
      return {
        background: `linear-gradient(135deg, ${C.seafoam}, ${C.teal})`,
        color: "#FFFFFF",
        border: "none",
      };
  }
}

export default function Button({
  children,
  variant = "primary",
  size = "md",
  fullWidth = false,
  disabled = false,
  type = "button",
  style = {},
  ...rest
}) {
  const sizeStyle = SIZES[size] || SIZES.md;
  const variantStyle = getVariantStyle(variant);
  return (
    <button
      type={type}
      disabled={disabled}
      {...rest}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        borderRadius: 12,
        fontFamily: "Georgia, serif",
        fontWeight: 700,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: "opacity 0.15s ease, transform 0.05s ease",
        ...sizeStyle,
        ...variantStyle,
        ...(fullWidth ? { width: "100%" } : {}),
        ...style,
      }}
    >
      {children}
    </button>
  );
}
